<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import draggable from 'vuedraggable';
import DraggableItem from './DraggableItem.vue';
import ReorderControls from './reorder/ReorderControls.vue';
import useReorder from './reorder/use-reorder';
import useLocale from './helpers/use-locale';
import useHaptics from './helpers/use-haptics';
import { MaybeElementRef, onClickOutside } from '@vueuse/core';
import { useDisplay } from 'vuetify';

const emit = defineEmits<{
  (e: 'update:modelValue', value: any[]): void;
  (e: 'clickItem', value: any): void;
  (e: 'reorderItem', value: { element: any; oldIndex: number; newIndex: number }): void;
  (e: 'reorderEnd'): void;
}>();

type Props = {
  modelValue: any[];
  itemKey?: string;
  draggableTag: string;
  wrapperRole?: string;
  itemRole: string;
  disabled?: boolean;
  extraClasses?: string[];
  labelKey?: string;
  getLabel?: (item: any) => Promise<string>;
  showControls?: boolean;
  touchHandle?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  showControls: true,
  touchHandle: false,
  labelKey: 'title',
  itemKey: 'id',
});

const { t } = useLocale();
const { platform } = useDisplay();

/**
 * A finger dragging anywhere on a row leaves no way to scroll the list it is reordering, so a list
 * that opts in restricts touch drags to the grip and lets the rest of the row scroll. A mouse keeps
 * the whole row either way: it has a wheel and nothing to lose.
 */
const dragHandle = computed(() => (props.touchHandle && platform.value.touch ? '.reorder-handle' : undefined));
const { light } = useHaptics();
let instanceCounter = 0;
const instanceId = (instanceCounter += 1);

const internalItems = ref([...props.modelValue]);
const wrapperRef = ref<any>(null);

const { assistiveText, announceMove, announceGrab, announceDrop, focusRow } = useReorder(
  () => wrapperRef.value?.$el ?? wrapperRef.value,
);

const grabbedKey = ref<string | null>(null);
const keyOf = (element: any) => String(element?.[props.itemKey]);
const grabbedIndex = computed(() =>
  grabbedKey.value === null ? null : internalItems.value.findIndex((item) => keyOf(item) === grabbedKey.value),
);
const isGrabbedElement = (element: any) => grabbedKey.value !== null && keyOf(element) === grabbedKey.value;

/**
 * Which row holds focus, by item key. A host that hands us a freshly built array — polylino rebuilds
 * its content on every change — makes Vue recreate the row elements, and focus dies with the old
 * node. Remembering the key lets it be put back on the row the user was actually on.
 */
const focusedKey = ref<string | null>(null);

const onFocusIn = (event: FocusEvent) => {
  const row = (event.target as HTMLElement | null)?.closest('.drag__item');
  const rows = Array.from((wrapperRef.value?.$el ?? wrapperRef.value)?.querySelectorAll('.drag__item') ?? []);
  const index = row ? rows.indexOf(row) : -1;
  focusedKey.value = index >= 0 ? keyOf(internalItems.value[index]) : null;
};

let grabStartIndex: number | null = null;
const instructionsId = computed(() => `reorder-instructions-${instanceId}`);

watch(
  () => props.modelValue,
  (val) => {
    internalItems.value = [...val];
    grabbedKey.value = null; // a list replaced under a grabbed row is no longer the list it grabbed

    if (focusedKey.value === null) {
      return;
    }
    const index = internalItems.value.findIndex((item) => keyOf(item) === focusedKey.value);
    nextTick(() => {
      // Only when the re-render actually dropped focus; never steal it from elsewhere on the page.
      if (index >= 0 && (document.activeElement === document.body || document.activeElement === null)) {
        focusRow(index);
      }
    });
  },
  { immediate: true },
);

const handleChange = async (event?: any) => {
  emit('update:modelValue', internalItems.value);

  const moved = event?.moved;
  if (!moved) {
    return;
  }
  emit('reorderItem', { element: moved.element, oldIndex: moved.oldIndex, newIndex: moved.newIndex });
  // A drag lands the same way a button move does, so it is announced and felt the same way too.
  light();
  announceMove(await getItemTitle(moved.element), moved.newIndex, internalItems.value.length);
};

const getItemTitle = async (item: any) => {
  if (typeof props.getLabel === 'function') {
    return await props.getLabel(item);
  }
  return item[props.labelKey];
};

const moveTo = async (from: number, to: number, commit = true) => {
  if (to < 0 || to >= internalItems.value.length || from === to) {
    return;
  }

  const itemsCopy = internalItems.value.slice();
  const [movedItem] = itemsCopy.splice(from, 1);
  itemsCopy.splice(to, 0, movedItem);
  internalItems.value = itemsCopy;

  // A row being carried is not saved on every step; putting it down is what commits the move.
  if (commit) {
    handleChange();
    emit('reorderItem', { element: movedItem, oldIndex: from, newIndex: to });
  }
  light();
  announceMove(await getItemTitle(movedItem), to, internalItems.value.length);
};

const moveItem = async (index: number, direction: -1 | 1) => {
  await moveTo(index, index + direction);
};

const moveFrom = (index: number) => (direction: -1 | 1) => moveItem(index, direction);
const grabFrom = (index: number) => () => toggleGrab(index);
const placeFrom = (index: number) => () => placeAt(index);

const putDown = async () => {
  const to = grabbedIndex.value;
  const from = grabStartIndex;
  grabbedKey.value = null;
  grabStartIndex = null;
  if (to === null) {
    return;
  }

  const item = internalItems.value[to];
  if (from !== null && from !== to && item) {
    handleChange();
    emit('reorderItem', { element: item, oldIndex: from, newIndex: to });
  }
  if (item) {
    announceDrop(await getItemTitle(item), to, internalItems.value.length);
  }
  emit('reorderEnd');
};

const toggleGrab = async (index: number) => {
  const item = internalItems.value[index];
  if (!item) {
    return;
  }
  if (grabbedIndex.value === index) {
    await putDown();
    return;
  }
  grabbedKey.value = keyOf(item);
  grabStartIndex = index;
  light();
  announceGrab(await getItemTitle(item), index, internalItems.value.length);
};

const moveGrabbed = (index: number, direction: -1 | 1) => {
  const hoverIndex = index + direction;
  if (hoverIndex < 0 || hoverIndex >= internalItems.value.length) {
    return;
  }
  moveTo(index, hoverIndex, false);
  focusRow(hoverIndex);
};

onClickOutside(wrapperRef as MaybeElementRef, () => {
  if (grabbedKey.value !== null) {
    putDown();
  }
});

const placeAt = async (to: number) => {
  const from = grabbedIndex.value;
  if (from === null) {
    return;
  }
  await moveTo(from, to, false);
  await putDown();
};

const onItemClick = (index: number, element: any) => {
  if (props.disabled) {
    handleClick(element);
    return;
  }
  if (grabbedIndex.value === null || grabbedIndex.value === index) {
    toggleGrab(index);
    return;
  }
  placeAt(index);
};

const handleClick = (item: any) => {
  emit('clickItem', item);
};
</script>

<template>
  <draggable
    ref="wrapperRef"
    v-model="internalItems"
    @change="handleChange"
    filter="input,select,textarea,button:not(.reorder-handle)"
    :handle="dragHandle"
    :prevent-on-filter="false"
    :force-fallback="true"
    :tag="draggableTag"
    :role="disabled ? null : wrapperRole"
    class="draggable-wrapper"
    :class="[extraClasses]"
    @focusin="onFocusIn"
    :disabled="disabled"
    :style="{ cursor: disabled ? 'pointer' : 'grab' }"
    :aria-label="t('components.label_list')"
    :item-key="itemKey"
  >
    <template #item="{ element, index }">
      <DraggableItem
        :index="index"
        :item="element"
        :role="itemRole"
        :disabled="disabled"
        :is-grabbed="isGrabbedElement(element)"
        :instructions-id="instructionsId"
        class="draggable-item"
        @click="onItemClick(index, element)"
        @toggle-grab="toggleGrab(index)"
        @move-item="moveGrabbed(index, $event)"
      >
        <div v-if="!disabled && showControls" class="d-flex align-center flex-nowrap">
          <ReorderControls :title="element[labelKey] ?? ''" :is-grabbed="isGrabbedElement(element)" />
          <div class="flex-grow-1 overflow-hidden">
            <slot
              name="draggableitem"
              :item="{ element, index }"
              :total="internalItems.length"
              :move="moveFrom(index)"
              :grab="grabFrom(index)"
              :place="placeFrom(index)"
              :is-grabbed="isGrabbedElement(element)"
              :has-grabbed="grabbedIndex !== null"
            ></slot>
          </div>
        </div>
        <slot
          v-else
          name="draggableitem"
          :item="{ element, index }"
          :total="internalItems.length"
          :move="moveFrom(index)"
          :grab="grabFrom(index)"
          :place="placeFrom(index)"
          :is-grabbed="isGrabbedElement(element)"
          :has-grabbed="grabbedIndex !== null"
        ></slot>
      </DraggableItem>
    </template>
  </draggable>

  <div v-if="!disabled" :id="instructionsId" class="d-sr-only">
    {{ t('components.reorder_instructions') }} {{ t('components.reorder_place_instructions') }}
  </div>
  <div aria-live="polite" class="d-sr-only" aria-atomic="true" v-text="assistiveText" />
</template>

<style scoped lang="scss">
ol {
  list-style: none;
}
.sortable-ghost {
  opacity: 0;
}
.sortable-chosen,
.sortable-ghost,
.sortable-fallback,
.sortable-drag,
.is-grabbed {
  background-color: rgba(var(--v-theme-outline), 0.1);
  border: 4px dashed rgb(var(--v-theme-primary));
}

.draggable-item:active,
.draggable-wrapper .is-grabbed,
.draggable-wrapper .sortable-chosen,
.draggable-wrapper .sortable-drag,
.draggable-wrapper:has(.sortable-chosen),
.draggable-wrapper:has(.sortable-drag) {
  cursor: grabbing !important;
}

/*
  Dim the rows that are not being carried. Written with `&` so the descendant combinator survives
  formatting: as plain multi-line selectors these once got split into standalone
  `.draggable-item:not(...)` rules, which dimmed and desaturated the whole list at rest.
*/
.draggable-wrapper {
  &:has(.sortable-chosen),
  &:has(.sortable-drag) {
    .draggable-item:not(.sortable-chosen):not(.sortable-ghost):not(.sortable-drag):not(.is-grabbed) {
      opacity: 0.5;
      mix-blend-mode: luminosity;
    }
  }
}
</style>
